/**
 * How routines are grouped on the surfaces that show more than one.
 *
 * Two levels, and they render in DIFFERENT places on purpose:
 *
 *   - The CONNECTOR (`sourceConnectorSlug`) is the top level, and it is the Routines screen's grouping —
 *     one card per app a kit came from, plus one card for everything the user or the agent made.
 *   - The SUB-GROUP (`groupKey`) is what the routine promises — briefing, chasing, welcoming — and it
 *     renders as a HEADING only where there is a single connector, which is the connector's own card in
 *     the conversation. On the Routines screen it is a SORT KEY and nothing else: nested under five
 *     connectors the same four headings would appear five times, and a heading that repeats five times is
 *     decoration rather than information. Ordering by it clusters the related rows inside each card at no
 *     cost in repeated titles.
 *
 * The IDs come from the genome's `corpus/params/routine-groups.json`; the words come from this app's own
 * locale catalog (`routines.group.<id>`). That split is the point rather than an optimisation — a routine
 * the agent shapes for one business is filed by the same key as one from a kit, so both land under one
 * heading instead of the genome's wording and the agent's wording splitting a group in half.
 *
 * Shared 1:1 with iOS `RoutineGroups.swift` and Android `RoutineGroups.kt`; the three test tables are the
 * same cases.
 */

/** The reading order, editorial rather than alphabetical. Mirrors `order` in the genome's params file. */
export const ROUTINE_GROUP_ORDER = [
  'replying',
  'needs-you',
  'briefing',
  'keeping-customers-posted',
  'before-the-visit',
  'after-the-visit',
  'chasing',
  'filling-the-calendar',
  'bringing-people-back',
  'welcoming',
  'general',
] as const;

export type RoutineGroupId = (typeof ROUTINE_GROUP_ORDER)[number];

/**
 * The title for a connector card the connectors screen cannot name, DERIVED from the slug.
 *
 * The genome's `corpus/connector/` grows a folder per kit, and every kit stamps its own slug onto the
 * routines it ships. So the card title has to come from the slug itself: a table of names would have to
 * be edited on three platforms every time a kit is added, and a kit missing from it is a card titled
 * `Home-assistant`.
 *
 * `wix-email-marketing` → `Wix Email Marketing`. Only a name the slug genuinely cannot produce needs an
 * entry in `CONNECTOR_NAME_OVERRIDES` — `wix-commerce` is the product's Wix Stores, and WordPress
 * carries a capital in the middle of the word.
 *
 * These are product names and stay in English, exactly like `Gmail` in `CONNECTOR_LABELS`. A CONNECTED
 * connector is named by the connectors payload instead, which is the vendor's own spelling.
 */
const CONNECTOR_NAME_OVERRIDES: Readonly<Record<string, string>> = {
  'wix-commerce': 'Wix Stores',
  wordpress: 'WordPress',
};

export function connectorSlugName(slug: string | undefined | null): string {
  const key = slug?.trim().toLowerCase();
  if (!key) return '';
  return (
    CONNECTOR_NAME_OVERRIDES[key] ??
    key
      .split(/[-_]+/)
      .filter(Boolean)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
  );
}

const FALLBACK: RoutineGroupId = 'general';

/**
 * The group to file a routine under. `general` covers both a routine created before the field existed
 * and one carrying a key this build does not know — a group the corpus added after this app shipped.
 * Never dropping the row is the whole reason the unknown case resolves rather than throws: a routine
 * missing from the screen is worse than one under a general heading.
 */
export function routineGroup(routine: { groupKey?: string }): RoutineGroupId {
  const key = routine.groupKey?.trim().toLowerCase();
  if (!key) return FALLBACK;
  return (ROUTINE_GROUP_ORDER as readonly string[]).includes(key) ? (key as RoutineGroupId) : FALLBACK;
}

/** The locale key for a group's heading. */
export function routineGroupTitleKey(group: RoutineGroupId): string {
  return `routines.group.${group}`;
}

function groupRank(routine: { groupKey?: string }): number {
  return ROUTINE_GROUP_ORDER.indexOf(routineGroup(routine));
}

/**
 * The routines in reading order within one connector: by group, then running before paused, then the
 * order the server sent. A stable sort throughout — two routines in the same group with the same pause
 * state keep the server's order rather than swapping places between renders.
 */
export function sortRoutinesForDisplay<R extends { groupKey?: string; paused?: boolean }>(
  routines: readonly R[],
): R[] {
  return routines
    .map((routine, index) => ({ routine, index }))
    .sort(
      (a, b) =>
        groupRank(a.routine) - groupRank(b.routine) ||
        Number(Boolean(a.routine.paused)) - Number(Boolean(b.routine.paused)) ||
        a.index - b.index,
    )
    .map((entry) => entry.routine);
}

/**
 * The routines belonging to a connector, matched against EVERY spelling it answers to and returned in
 * reading order.
 *
 * A routine's `sourceConnectorSlug` is whatever the agent passed as the tool's `connector`, and the tool
 * asks for "the folder that holds its skill in best-practices". For Instagram and Facebook that folder is
 * the CATEGORY, `connector/social/`, not the toolkit — so the three Instagram routines a band installs
 * are filed under `social` while the band card looks its apps up by `instagram`. An exact match found
 * nothing, and the card's social row read "Connected" with no count and no chevron while Gmail, whose
 * folder IS its toolkit, showed "1 routine on". Passing the app's group key alongside its toolkit is
 * correct whichever spelling the agent used, and dedupes when two apps of one group fold into a row.
 *
 * Shared 1:1 with iOS `connectorRoutines(for:)` and Android `connectorRoutinesForSlugs`.
 */
export function connectorRoutinesForSlugs<R extends { groupKey?: string; paused?: boolean; sourceConnectorSlug?: string }>(
  routines: readonly R[],
  slugs: readonly (string | undefined)[],
): R[] {
  const wanted = new Set(slugs.filter((slug): slug is string => !!slug));
  return sortRoutinesForDisplay(routines.filter((routine) => wanted.has(routine.sourceConnectorSlug ?? '')));
}

/** One section per connector, in the order `slugs` gives, with the routines nobody's kit shipped last. */
export interface RoutineConnectorSection<R> {
  /** The connector's slug, or undefined for the routines the user and the agent made. */
  slug?: string;
  routines: R[];
}

/**
 * Split routines into their connector sections.
 *
 * EVERY slug gets a section. `connectedSlugs` is only the ORDER for the ones the connectors screen
 * shows — its own order, so the two screens agree — and a slug missing from it follows in the order the
 * server sent its routines. The unattributed section, for what the user and the agent made themselves,
 * is always last.
 *
 * The slug alone is enough to head a card, because a kit only reaches an account that has the app: a
 * Composio toolkit it connected, or a Wix vertical installed on its business site (`wixAppCategoryMap`
 * in the genome's `capabilities.json`). Only the app's own NAME needs the connectors read — an unlisted
 * slug is titled from itself (`connectorSlugName`) — and only the connectors screen can say whether it
 * is still connected, which is a different question from where a routine came from.
 *
 * This is what makes the next kit work with no edit here: the genome adds a `corpus/connector/` folder,
 * its routines carry the new slug, and they group under their own card on all three surfaces.
 */
export function routineConnectorSections<R extends { sourceConnectorSlug?: string; groupKey?: string; paused?: boolean }>(
  routines: readonly R[],
  connectedSlugs: readonly string[],
): RoutineConnectorSection<R>[] {
  const known = new Map<string, R[]>();
  for (const slug of connectedSlugs) {
    const key = slug.trim().toLowerCase();
    if (key) known.set(key, []);
  }
  const rest: R[] = [];

  for (const routine of routines) {
    const slug = routine.sourceConnectorSlug?.trim().toLowerCase();
    if (!slug) {
      rest.push(routine);
      continue;
    }
    const bucket = known.get(slug) ?? [];
    if (!known.has(slug)) known.set(slug, bucket);
    bucket.push(routine);
  }

  const sections: RoutineConnectorSection<R>[] = [];
  for (const [slug, group] of known) {
    if (group.length > 0) sections.push({ slug, routines: sortRoutinesForDisplay(group) });
  }
  if (rest.length > 0) sections.push({ routines: sortRoutinesForDisplay(rest) });
  return sections;
}

/** One section per sub-group, for the surface that shows a SINGLE connector. */
export interface RoutineGroupSection<R> {
  group: RoutineGroupId;
  routines: R[];
}

/**
 * Split one connector's routines into their sub-group sections, in reading order. Only the groups that
 * have a routine get a section — an empty heading is a promise the account cannot see kept.
 */
export function routineGroupSections<R extends { groupKey?: string; paused?: boolean }>(
  routines: readonly R[],
): RoutineGroupSection<R>[] {
  const sections: RoutineGroupSection<R>[] = [];
  for (const routine of sortRoutinesForDisplay(routines)) {
    const group = routineGroup(routine);
    const last = sections[sections.length - 1];
    if (last && last.group === group) last.routines.push(routine);
    else sections.push({ group, routines: [routine] });
  }
  return sections;
}
